import React, { useEffect, useRef } from 'react';
import { Mail, Phone, GraduationCap, Hash, Calendar, Trash2, Users, CheckCircle, Circle, Lock } from 'lucide-react';
import { API_URL } from '../config';

function MembersList({ members = [], onDelete, onUpdate, onRefresh, userRole }) {
  const intervalRef = useRef(null);

  // Canlı senkronizasyon - her 5 saniyede bir üye listesini yenile
  useEffect(() => {
    if (onRefresh) {
      intervalRef.current = setInterval(() => {
        onRefresh();
      }, 5000); // 5 saniye
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [onRefresh]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleCheckboxChange = async (memberId, field, currentValue) => {
    // ACM Kayıt sadece super_user değiştirebilir
    if (field === 'acmRegistered' && userRole !== 'super_user') {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/members?id=${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ [field]: !currentValue })
      });

      if (response.ok && onUpdate) {
        onUpdate(memberId, field, !currentValue);
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
    }
  };

  if (members.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
        <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">Henüz üye kaydı yok</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {members.map(member => (
        <div key={member.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 text-sm font-medium flex-shrink-0">
                {member.fullName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{member.fullName}</p>
                <p className="text-zinc-500 text-xs truncate">{member.department}</p>
              </div>
            </div>
            <button onClick={() => onDelete && onDelete(member.id)} className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0" title="Sil">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Mail className="w-3 h-3 text-zinc-600" />
              <span className={`truncate ${member.email?.includes('@acm.local') ? 'italic text-zinc-500' : ''}`}>
                {member.email?.includes('@acm.local') ? 'Belirtilmedi' : member.email}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Phone className="w-3 h-3 text-zinc-600" />
              <span>{member.phone || '-'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-400">
              <GraduationCap className="w-3 h-3 text-zinc-600" />
              <span>{member.class}. Sınıf</span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Hash className="w-3 h-3 text-zinc-600" />
              <span>{member.schoolNumber}</span>
            </div>
          </div>

          {/* Kayıt Kontrol Checkbox'ları */}
          <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-3">
            {/* ACM Kayıt - Sadece super_user değiştirebilir */}
            {userRole === 'super_user' ? (
              <button
                onClick={() => handleCheckboxChange(member.id, 'acmRegistered', member.acmRegistered)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all ${member.acmRegistered
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:border-zinc-600'
                  }`}
              >
                {member.acmRegistered ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span>ACM Kayıt</span>
              </button>
            ) : (
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs cursor-not-allowed ${member.acmRegistered
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 opacity-60'
                  : 'bg-zinc-800 text-zinc-500 border border-zinc-700 opacity-60'
                  }`}
                title="Sadece süper yönetici değiştirebilir"
              >
                {member.acmRegistered ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span>ACM Kayıt</span>
                <Lock className="w-3 h-3 ml-1" />
              </div>
            )}

            {/* SKS Kayıt - Tüm adminler değiştirebilir */}
            <button
              onClick={() => handleCheckboxChange(member.id, 'sksRegistered', member.sksRegistered)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all ${member.sksRegistered
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:border-zinc-600'
                }`}
            >
              {member.sksRegistered ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
              <span>SKS Kayıt</span>
            </button>
          </div>

          <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
            {userRole === 'super_user' ? (
              <span className="truncate">{member.referral}</span>
            ) : (
              <span className="truncate">
                {member.referral?.includes('tarafından eklendi') ? 'Yönetici tarafından kaydedildi' : member.referral}
              </span>
            )}
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(member.date || member.createdAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MembersList;
